/**
 * A solved scene, read into plain numbers. Every extent comes from what the engine already computed, never from authored sizes: the
 * size the solver gave each Layout container (`Layout.getSolvedSize`), `measureTextBox` at the wrap width layout resolved (the
 * arithmetic the Text behavior hugs with, so headless and browser agree), and the visible meshes and the unit plane of an Image under
 * the scale layout wrote into the node.
 * Selection and collider helper meshes are never read: they are pick boxes centred on the origin, not the drawn glyphs.
 */
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Node } from '@babylonjs/core/node';
import type { TraceScene } from '../../core/TraceScene';
import type { SceneObject } from '../../core/SceneObject';
import { Billboard, ContentSlot, Image, Layout, LayoutItem, Renderer, Text } from '../../exports/behaviors';
import type { LayoutData, LayoutItemData, TextData } from '../../exports/behaviors';
import { DEFAULT_TEXT_FAMILY, DEFAULT_TEXT_WEIGHT, measureTextBox } from '../../exports/layout';
import { missingGlyphs } from '../../behaviors/text/measure';
import type { TextMeasureInput } from '../../exports/layout';
import { COLLIDER_MESH_SUFFIX, SELECTION_MESH_SUFFIX } from '../../behaviors/boxcollider';
import { boundingBox, boxFromMatrix } from './geometry';
import type { Box } from './geometry';
import type { MeasuredNode, MeasuredScene, MeasuredText, NodeKind } from './types';

/** The Layout default (`LayoutData.padding`, `@default 0.02`) when a container authors none. */
const DEFAULT_PADDING = 0.02;
/** A mesh whose thinnest side is under this share of its middle side is a panel, not a 3D object. */
const PANEL_THINNESS = 0.2;

const finite = (value: unknown, fallback: number): number => typeof value === 'number' && Number.isFinite(value) ? value : fallback;

function parseColor(value: unknown): { hex: string; alpha: number } | null {
    if (typeof value !== 'string') return null;
    const match = /^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/.exec(value);
    return match ? { hex: `#${match[1].toLowerCase()}`, alpha: match[2] ? parseInt(match[2], 16) / 255 : 1 } : null;
}

/** A mesh the user can see, as opposed to a pick box, a collider or an editor handle. Same test as the Model's geometry provider. */
export function isDrawn(mesh: AbstractMesh): boolean {
    if (!mesh.isVisible || mesh.visibility <= 0 || !mesh.isEnabled() || mesh.getTotalVertices() === 0) return false;
    const name = mesh.name;
    if ((name.startsWith('__') && !name.startsWith('__builtin_')) || name.endsWith(COLLIDER_MESH_SUFFIX) || name.endsWith(SELECTION_MESH_SUFFIX)) return false;
    for (let node: Node | null = mesh; node; node = node.parent) {
        const flags = node.metadata as Record<string, unknown> | null | undefined;
        if (node.doNotSerialize || flags?.editorOnly || flags?.editorHandle || flags?.doNotSerialize || flags?.hidden) return false;
    }
    return true;
}

function meshBox(mesh: AbstractMesh): Box {
    const local = mesh.getBoundingInfo().boundingBox;
    return boxFromMatrix(mesh.computeWorldMatrix(true).m, local.center.asArray(), local.extendSize.asArray());
}

function worldMatrix(so: SceneObject): ArrayLike<number> {
    return so.transform.computeWorldMatrix(true).m;
}

function measureText(so: SceneObject, text: Text): { box: Box | null; text: MeasuredText } {
    const data = text._getResolvedData() as TextData & { fontStyle?: TextMeasureInput['fontStyle'] };
    const m = worldMatrix(so);
    const sx = Math.hypot(m[0], m[1], m[2]), sy = Math.hypot(m[4], m[5], m[6]);
    const baked = (text.serialize()).bakedWrapWidth;
    const content = typeof data.text === 'string' ? data.text : '';
    const input: TextMeasureInput = {
        text: content,
        fontSize: finite(data.fontSize, 14),
        lineHeight: finite(data.lineHeight, 1.2),
        letterSpacing: finite(data.letterSpacing, 0),
        anchorX: data.anchorX ?? 'center',
        anchorY: data.anchorY ?? 'middle',
        fontFamily: typeof data.fontFamily === 'string' ? data.fontFamily : undefined,
        fontWeight: data.fontWeight,
        fontStyle: data.fontStyle,
        wrap: data.wrap,
        wrapWidth: baked ?? null,
    };
    const measured = measureTextBox(input);
    let longestWordM = 0, longestWord = '';
    for (const word of content.split(/\s+/)) {
        if (!word) continue;
        const width = measureTextBox({ ...input, text: word, wrapWidth: null, wrap: false }).width;
        if (width > longestWordM) { longestWordM = width; longestWord = word; }
    }
    const color = parseColor(data.color);
    return {
        box: content.trim() && measured.width > 0 && measured.height > 0
            ? boxFromMatrix(m, [measured.centerX, measured.centerY, 0], [measured.width / 2, measured.height / 2, 0]) : null,
        text: {
            content, emM: input.fontSize / 100 * sy, widthM: measured.width * sx, heightM: measured.height * sy,
            lineCount: measured.lineCount, wrapWidthM: baked !== undefined && baked > 0 ? baked * sx : null,
            longestWordM: longestWordM * sx, longestWord, color,
            missingGlyphs: missingGlyphs(content, input),
            face: `${input.fontFamily ?? DEFAULT_TEXT_FAMILY} ${input.fontWeight ?? DEFAULT_TEXT_WEIGHT}`,
        },
    };
}

function containerPadding(data: LayoutData): readonly [number, number, number, number] {
    const p = data.padding;
    if (typeof p === 'number') return [p, p, p, p];
    if (p && typeof p === 'object') return [finite(p.left, 0), finite(p.right, 0), finite(p.top, 0), finite(p.bottom, 0)];
    return [DEFAULT_PADDING, DEFAULT_PADDING, DEFAULT_PADDING, DEFAULT_PADDING];
}

function thinness(box: Box): number {
    const [thin, mid] = [...box.half].sort((a, b) => a - b);
    return mid > 0 ? thin / mid : 1;
}

function surface(so: SceneObject): { hex: string; alpha: number } | undefined {
    const renderer = so.getBehavior(Renderer)?._getResolvedData();
    const face = renderer?.materials?.[0];
    const color = parseColor(face?.baseColorFactor);
    return color ? { hex: color.hex, alpha: color.alpha * finite(renderer?.opacity, 1) } : undefined;
}

/** What an object and everything active under it draw: text line boxes and visible meshes, world-axis aligned; null when nothing draws. */
export function drawnBox(so: SceneObject): Box | null {
    const boxes: Box[] = [];
    const visit = (o: SceneObject): void => {
        if (!o.effectiveActive) return;
        const text = o.getBehavior(Text);
        const box = text ? measureText(o, text).box : null;
        if (box) boxes.push(box);
        else if (!text) boxes.push(...o.getOwnedMeshes().filter(isDrawn).map(meshBox));
        o.getChildren().forEach(visit);
    };
    visit(so);
    return boundingBox(boxes);
}

/** Read every active node of a solved scene. Await `awaitLayoutQuiescence` first: this reads, it never solves. */
export function measureScene(traceScene: TraceScene): MeasuredScene {
    const nodes: MeasuredNode[] = [];
    const visit = (so: SceneObject, parent: number, parentIsLayout: boolean, underBillboard: boolean): void => {
        if (!so.effectiveActive) return;
        const text = so.getBehavior(Text);
        const layout = so.getBehavior(Layout);
        const item = so.getBehavior(LayoutItem);
        const itemData = (item ?? layout)?._getResolvedData() as LayoutItemData | undefined;
        const callout = underBillboard || !!so.getBehavior(Billboard);
        let kind: NodeKind = 'group';
        let box: Box | null = null;
        const node: Partial<MeasuredNode> = {};
        if (text) {
            kind = 'text';
            const measured = measureText(so, text);
            box = measured.box;
            node.text = measured.text;
        } else if (layout) {
            kind = 'container';
            const data = layout._getResolvedData() as LayoutData;
            // The size the solver gave the container, not its authored width/height: a Fill or stretched container's data holds a
            // basis, and a clamped one an authored size it did not get. A childless container is never solved; its data is all there is.
            const solved = layout.getSolvedSize() ?? (so.getChildren().length === 0 ? { x: finite(data.width, NaN), y: finite(data.height, NaN), z: finite(data.depth, 0) } : null);
            if (solved && [solved.x, solved.y].every(v => Number.isFinite(v) && v >= 0)) {
                box = boxFromMatrix(worldMatrix(so), [0, 0, 0], [solved.x / 2, solved.y / 2, solved.z / 2]);
            }
            // The box is in world metres, so its padding is too: a container scaled by a slot's fit holds its padding at that scale.
            const m = worldMatrix(so);
            const sx = Math.hypot(m[0], m[1], m[2]), sy = Math.hypot(m[4], m[5], m[6]);
            const [pl, pr, pt, pb] = containerPadding(data);
            node.padding = [pl * sx, pr * sx, pt * sy, pb * sy];
            if (so.getBehavior(ContentSlot)) node.slot = true;
        } else if (so.getBehavior(Image)) {
            kind = 'image';
            box = boxFromMatrix(worldMatrix(so), [0, 0, 0], [0.5, 0.5, 0]);
        } else {
            const meshes = so.getOwnedMeshes().filter(isDrawn);
            if (meshes.length > 0) {
                box = meshes.length === 1 ? meshBox(meshes[0]) : boundingBox(meshes.map(meshBox));
                kind = box && thinness(box) <= PANEL_THINNESS ? 'panel' : 'solid';
                if (kind === 'panel') node.fill = surface(so);
            }
        }
        const index = nodes.length;
        nodes.push({
            path: so.getPath(), parent, kind, box,
            flow: parentIsLayout && itemData?.positionMode !== 'Absolute',
            absolute: itemData?.positionMode === 'Absolute',
            callout, ...node,
        });
        for (const child of so.getChildren()) visit(child, index, !!layout, callout);
    };
    for (const root of traceScene.getSceneObjects().filter(so => !so.getParent())) visit(root, -1, false, false);
    return { nodes };
}
